import { DevicesStateActions } from '../store/devices-reducer'
import { ToolkitStore } from '@reduxjs/toolkit/dist/configureStore'
import DeviceRepository, {
    AddMeterPayload,
    Device,
    DevicesFilter,
    MeterCategory,
    ThirdPartyVendor,
    VerifyDeviceParams,
    PrepaidDeviceNotFoundReportPayload,
    MeterBillingType,
    MeterType,
    Meter,
    MeterStatus,
    LowBalanceLimitRequest,
    UpdateLowBalanceLimitRequest,
    UpdatePreviousMeterRequest,
    MMS_METER_TYPES,
} from '../repositories/devices-repository'
import { RootState } from '@/store'
import balanceEducationDataJson from '@/dummy-data/meter-balance-edu.json'

export interface MeterGroup {
    meters: Meter[]
    allMeters: Meter[]
    inStoreVendors: (ThirdPartyVendor | Meter)[]
    energyMeters: Meter[]
    nonEnergyMeters: Meter[]
}

export interface BalanceEducationData {
    meterType: string
    meterImage: string
    balanceCheckSteps: string
}

export default class DevicesService {
    constructor(
        private deviceRepository: DeviceRepository,
        private store: ToolkitStore<RootState>,
        private devicesStateActions: DevicesStateActions,
    ) {}
    isthirdPartyVendorMeter(meter?: ThirdPartyVendor | Device): meter is ThirdPartyVendor {
        return (<ThirdPartyVendor>meter)?.vendorId !== undefined
    }

    isSLTMeter(meter: Device) {
        return this.getMeterCategory(meter) === MeterCategory.SLT
    }

    isPostpaidOrSLTMeterOrFlatRate(meter: Device) {
        return (
            this.getMeterCategory(meter) === MeterCategory.POSTPAID ||
            this.isSLTMeter(meter) ||
            this.isFlatRateMeter(meter)
        )
    }

    isPrepaidMeter(meter?: Device) {
        return this.getMeterCategory(meter) === MeterCategory.PREPAID && !this.isFlatRateMeter(meter)
    }

    isFlatRateMeter(meter?: Device) {
        return meter?.billingType === MeterBillingType.FLATRATE
    }

    isNonEnergyMeter(meter: Device) {
        return this.getMeterCategory(meter) === MeterCategory.NON_ENERGY
    }

    async isMeterOnline(meter: Device) {
        if (this.isAKnownOfflineMeter(meter)) return false
        const meters = await this.getMeterBrands()
        const matchingBrand = meters.data
            .filter(brand => brand.name.toLowerCase() === meter.meterType?.toLowerCase())
            .pop()
        if (matchingBrand) return matchingBrand.online
        return false
    }

    isAKnownOfflineMeter(meter?: Device) {
        const knownOfflineMeters = ['bxc', 'bot', 'e-cash 3', 'gem', 'holley']
        return knownOfflineMeters.includes(meter?.meterType?.toLowerCase() ?? '')
    }

    getMeterCategory(meter?: Device) {
        if (this.isthirdPartyVendorMeter(meter)) return MeterCategory.THIRD_PARTY
        return meter?.meterCategory?.toLowerCase()
    }

    calculateDefaultInvoiceValueForSLTMeter(meter: Device) {
        if (this.getMeterCategory(meter) !== MeterCategory.SLT) return ''
        return 0.7 * parseFloat(meter.balance ?? '0')
    }

    async getAllDevices(
        filter: Partial<DevicesFilter> = {
            Page: 1,
            PageSize: 500,
        },
    ) {
        const metersResponse = await this.deviceRepository.getAllDevices(filter)
        this.store.dispatch(
            this.devicesStateActions.setState({
                meters: metersResponse.data.results,
            }),
        )
        return metersResponse
    }

    async getSingleDeviceWithFilters(
        filter: Partial<DevicesFilter> = {
            Page: 1,
            PageSize: 1,
        },
    ) {
        const metersResponse = await this.deviceRepository.getAllDevices(filter)
        return metersResponse
    }

    async getAllDeviceAccounts() {
        this.store.dispatch(this.devicesStateActions.setIsFetchingMeters(true))

        const metersResponse = await this.deviceRepository.getAllDeviceAccounts()

        const energyMeters = metersResponse.data.meters.filter(
            meter =>
                meter.meterCategory?.toLowerCase() !== MeterCategory.NON_ENERGY &&
                meter.meterType?.toLowerCase() !== MeterType.NON_ENERGY &&
                meter.meterStatus?.toLowerCase() !== 'pending review' &&
                meter.meterStatus?.toLowerCase() !== 'disconnected',
        )

        const nonEnergyMeters = metersResponse.data.meters.filter(
            meter =>
                meter.meterCategory?.toLowerCase() === MeterCategory.NON_ENERGY &&
                meter.meterType?.toLowerCase() === MeterType.NON_ENERGY,
        )

        this.store.dispatch(
            this.devicesStateActions.setState({
                meters: metersResponse.data.meters,
                inStoreVendors: metersResponse.data.inStoreVendors ?? [],
                allMeters: [...metersResponse.data.meters, ...(metersResponse.data.inStoreVendors ?? [])],
                energyMeters: energyMeters,
                nonEnergyMeters: nonEnergyMeters,
                isFetchingMeters: false,
            }),
        )
        return metersResponse
    }

    async getAllDevicesWithToken(token: string) {
        return await this.deviceRepository.getAllDevicesWithToken(token)
    }

    async getMetersInGroups(token?: string): Promise<MeterGroup | void> {
        this.store.dispatch(this.devicesStateActions.setIsFetchingMeters(true))
        try {
            if (!token) {
                const res = await this.getAllDeviceAccounts()

                const data = {
                    meters: res.data.meters,
                    allMeters: [...res.data.meters, ...(res.data.inStoreVendors ?? [])],
                    inStoreVendors: res.data.inStoreVendors ?? [],
                    energyMeters: this.getEnergyMeters(res.data.meters),
                    nonEnergyMeters: this.getNonEnergyMeters(res.data.meters),
                }

                this.store.dispatch(
                    this.devicesStateActions.setState({
                        ...data,
                        isFetchingMeters: false,
                    }),
                )

                return data as MeterGroup
            } else {
                const res = await this.getAllDevicesWithToken(token)

                const data = {
                    meters: res.data as Meter[],
                    allMeters: res.data,
                    energyMeters: this.getEnergyMeters(res.data as Meter[]),
                }

                this.store.dispatch(
                    this.devicesStateActions.setState({
                        ...data,
                        isFetchingMeters: false,
                    }),
                )

                return data as MeterGroup
            }
        } finally {
            this.store.dispatch(this.devicesStateActions.setIsFetchingMeters(false))
        }
    }

    async getDeviceByAccountNumber(accountNumber: string) {
        const res = await this.deviceRepository.getAllDevices({
            AccountNumber: accountNumber,
        })

        return res.data.results.pop()
    }

    getEnergyMeters(meters: Meter[]) {
        return meters.filter(
            meter =>
                meter.meterCategory?.toLowerCase() !== MeterCategory.NON_ENERGY &&
                meter.meterType?.toLowerCase() !== MeterType.NON_ENERGY &&
                meter.meterStatus?.toLowerCase() !== 'pending review' &&
                meter.meterStatus?.toLowerCase() !== 'disconnected',
        )
    }

    getNonEnergyMeters(meters: Meter[]) {
        return meters.filter(
            meter =>
                meter.meterCategory?.toLowerCase() === MeterCategory.NON_ENERGY &&
                meter.meterType?.toLowerCase() === MeterType.NON_ENERGY,
        )
    }

    getDeviceByIdFromState(id: string) {
        return this.store.getState().meters.allMeters.find(meter => meter.id === id)
    }

    getSingleDevice(id: string) {
        return this.deviceRepository.getSingleDevices(id)
    }
    getPrepaidDeviceMlBalance(id: string) {
        return this.deviceRepository.getPrepaidDeviceMlBalance(id)
    }

    getDeviceBalance(id: string) {
        return this.deviceRepository.getDeviceBalance(id)
    }

    updateDevices(id: string, payload: Partial<Device>) {
        return this.deviceRepository.updateDevice(id, payload)
    }

    deleteDevices(meter: Device) {
        if (this.isthirdPartyVendorMeter(meter)) {
            return this.deviceRepository.deleteThirdPartyMeter(meter.id)
        } else {
            return this.deviceRepository.deleteDevice(meter.id)
        }
    }

    addDevice(AddMeterPayload: AddMeterPayload) {
        return this.deviceRepository.addDevice(AddMeterPayload)
    }

    updatePreviousMeter(payload: UpdatePreviousMeterRequest) {
        return this.deviceRepository.updatePreviousMeter(payload)
    }

    addThirdPartyVendor(payload: { meterType: string; userId: string }) {
        return this.deviceRepository.addThirdPartyVendor(payload)
    }

    verifyThirdPartyVendorId(payload: { meterType: string; userId: string }) {
        return this.deviceRepository.verifyThirdPartyVendorId(payload)
    }

    getThirdPartyVendors() {
        return this.deviceRepository.getThirdPartyVendor()
    }

    getThirdPartyMeterDetails(id: string | number) {
        return this.deviceRepository.getThirdPartyMeterDetails(id)
    }

    getThirdPartyMeterSuppliers() {
        return this.deviceRepository.getThirdPartyMeterSuppliers()
    }

    getMeterBrands() {
        return this.deviceRepository.getMeterBrands()
    }

    verifyGhanaPostGPSLocation(ghanaPostGPSAddress: string) {
        return this.deviceRepository.verifyGhanaPostGPSLocation(ghanaPostGPSAddress)
    }

    getGhanaPostCodeFromRawGPS(lat: string, long: string) {
        return this.deviceRepository.getGhanaPostCodeFromRawGPS(lat, long)
    }

    verifyDeviceNumber(payload: VerifyDeviceParams) {
        return this.deviceRepository.verifyDevice(payload)
    }

    getDeviceRegions() {
        return this.deviceRepository.deviceRegions()
    }

    async getDeviceDistricts(code: string) {
        return await this.deviceRepository.deviceDistricts(code)
    }

    async sendPrepaidDeviceNotFoundReport(payload: PrepaidDeviceNotFoundReportPayload) {
        return await this.deviceRepository.createPrepaidDeviceNotFoundReport(payload)
    }

    async addServiceRequestAsMeter(payload: { accountNumber: string }) {
        return await this.deviceRepository.addServiceRequestAsMeter(payload)
    }

    async getNextPurchasePrediction(accountNumber: string) {
        return await this.deviceRepository.getNextPurchasePrediction(accountNumber)
    }

    getMeterCardText(meterStatus?: MeterStatus | null): string {
        if (!meterStatus) {
            return ''
        }
        const status = meterStatus?.toLowerCase()
        const map: Record<string, string> = {
            [MeterStatus.DISCONNECTED]: 'Temporarily Disconnected',
            [MeterStatus.PENDING_REVIEW]: 'Pending Review',
        }
        return map[status]
    }

    async getDeviceLimitCheck() {
        return await this.deviceRepository.getDeviceLimitCheck()
    }

    async addLowBalanceLimit(payload: LowBalanceLimitRequest) {
        return await this.deviceRepository.addLowBalanceLimit(payload)
    }

    async updateLowBalanceLimit(payload: UpdateLowBalanceLimitRequest) {
        return await this.deviceRepository.updateLowBalanceLimit(payload)
    }

    async getDeviceDebtStatus(meterId: string) {
        return await this.deviceRepository.getDeviceDebtStatus(meterId)
    }

    async getPreviousBalance(meterId: string) {
        return await this.deviceRepository.getPreviousBalance(meterId)
    }

    hasOutstandingDebt(meter: Meter) {
        const debtDueDate = meter.debtDueDate
        const isDebtOutsanding = !!debtDueDate
        return meter.hasDebt && meter.debtAmount && !meter.debtAmountPaid && isDebtOutsanding
    }

    hasOverdueDebt(meter: Meter) {
        const debtDueDate = meter.debtDueDate
        const now = Date.now()
        const isDebtOverdue = debtDueDate && new Date(debtDueDate).getTime() <= now

        return meter.hasDebt && meter.debtAmount && !meter.debtAmountPaid && isDebtOverdue
    }

    deviceHasBalanceEducation(meter: Meter | Device): boolean {
        const meterHasBalanceEducation = balanceEducationDataJson?.devices.some(
            data => data.meterType.toLowerCase() === meter.meterType?.toLowerCase(),
        )
        return meterHasBalanceEducation ?? balanceEducationDataJson.default
    }

    deviceHasRemoteBalance(meter: Meter | Device): boolean {
        return meter.balance !== null
    }

    getDeviceBalanceEducationData(meterType: string): BalanceEducationData | undefined {
        const data = balanceEducationDataJson?.devices.find(
            data => data.meterType.toLowerCase() === meterType.toLowerCase(),
        )
        return data ?? balanceEducationDataJson.default
    }

    isMeterPendingReview(meter: Meter): boolean {
        const pendingStatuses = [MeterStatus.PENDING, MeterStatus.PENDING_REVIEW]

        // Get the effective status, prioritizing meterStatus over replacementStatus
        const currentStatus = (meter?.meterStatus ?? meter?.replacementStatus ?? '').toLowerCase()

        // Check if the current status matches any pending status
        return pendingStatuses.includes(currentStatus as MeterStatus)
    }

    updateMeterInstallationState(payload: { deviceId: string; transactionId: string }) {
        return this.deviceRepository.updateMeterInstallationState(payload)
    }

    isMmsDevice(meter: Meter): boolean {
        return MMS_METER_TYPES.includes(meter.meterType ?? '')
    }

    isImesDevice(meter: Meter): boolean {
        return ['imes'].includes(meter.meterType?.toLowerCase() ?? '')
    }

    isNegativeBalanceDevice(meter: Meter): boolean {
        return this.isMmsDevice(meter) || this.isImesDevice(meter)
    }

    async getPostpaidDebts() {
        return await this.deviceRepository.getPostpaidDebt()
    }

    async getVendorDebts() {
        return await this.deviceRepository.getVendorDebts()
    }
}
